"use client";

import { useEffect, useState } from "react";
import { Menu, Plus, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CartIcon } from "@/components/shop/CartIcon";
import { Link } from "@/components/common/Link";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { ProductsSearch } from "@/components/shop/ProductsSearch";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex items-center gap-8">
        {[0, 1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-3 w-16 bg-white/10 animate-pulse" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative flex items-center text-foreground text-[16px] font-default font-normal uppercase tracking-[0.01em] px-0 py-1 hover:bg-transparent focus:bg-transparent data-[active=true]:bg-transparent">
                <Plus className="absolute -left-4 size-3 text-secondary-foreground opacity-0 -translate-x-1 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0" strokeWidth={2.5} />
                <span>{item.label}</span>
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-accent transition-all duration-300 ease-out group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6">
        {[0, 1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-10 w-3/4 bg-white/10 animate-pulse" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-2">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={onNavigate} className="group flex items-baseline gap-4 border-b border-primary py-4 text-foreground">
          <span className="font-mono text-xs text-secondary tracking-widest">
            {String(i + 1).padStart(2, "0")}
          </span>
          <span className="text-3xl sm:text-4xl font-default font-bold uppercase tracking-tight leading-none group-hover:text-secondary transition-colors duration-200">
            {item.label}
          </span>
        </Link>)}
    </nav>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      if (typeof window !== "undefined") {
        setScrolled(window.scrollY > 20);
      }
    };
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      try {
        const target = e.target as HTMLElement;
        if (!target) return;
        const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
        if (!anchor) return;
        const href = anchor.getAttribute("href");
        if (!href || typeof window === "undefined" || typeof document === "undefined") return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            requestAnimationFrame(() => {
              element.scrollIntoView({
                behavior: "smooth",
                block: "start"
              });
            });
          }
        }
      } catch (error) {
        console.error("Error handling anchor click:", error);
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="3061" id="main-navigation-header" className={`sticky top-0 z-50 w-full bg-background text-foreground border-b transition-all duration-300 ${scrolled ? "bg-background/90 backdrop-blur-sm border-primary" : "border-primary"}`}>
      <div className="mx-auto w-full max-w-[1400px] px-4 sm:px-6 lg:px-10">
        <div className="flex h-16 lg:h-20 items-center justify-between gap-4">
          <div className="flex items-center gap-3 shrink-0">
            <Link to="/" className="flex items-center" aria-label="Oddhatter home">
              <WvcLogo className="h-7 w-auto" />
            </Link>
            <div className="hidden md:flex flex-col leading-none border-l border-primary pl-3">
              <span className="font-default text-[10px] tracking-[0.22em] uppercase text-foreground">
                // ODDHATTER
              </span>
              <span className="font-default text-[10px] tracking-[0.22em] uppercase text-primary mt-0.5">
                ENTERTAINMENT
              </span>
            </div>
          </div>

          <div className="hidden lg:flex flex-1 items-center justify-center">
            <MenuProvider menu_id="43" className="flex items-center">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <Button type="button" variant="ghost" size="icon" onClick={() => setSearchOpen(v => !v)} className="text-foreground hover:bg-secondary hover:text-white" aria-label="Toggle search">
              {searchOpen ? <X className="size-5" strokeWidth={1.5} /> : <Search className="size-5" strokeWidth={1.5} />}
            </Button>

            <span className="hidden sm:block h-6 w-px bg-primary" aria-hidden />

            <div className="relative">
              <CartIcon variant="ghost" size="icon" iconSize="size-5" className="text-foreground hover:bg-secondary hover:text-white [&>span]:!bg-secondary [&>span]:!text-white [&>span]:!rounded-md" />
            </div>

            <Button asChild className="hidden md:inline-flex bg-background text-white border-2 border-foreground hover:bg-secondary rounded-md uppercase tracking-[0.18em] font-default font-medium text-[16px] h-10 px-6">
              <Link to="/ShopPage">Get Tickets</Link>
            </Button>

            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button type="button" variant="ghost" size="icon" className="lg:hidden text-foreground hover:bg-secondary hover:text-white" aria-label="Open menu">
                  <Menu className="size-5" strokeWidth={1.5} />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:max-w-full bg-background text-foreground border-l border-primary p-0">
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                <div className="flex h-full flex-col">
                  <div className="flex items-center justify-between px-6 py-5 border-b border-primary">
                    <div className="flex items-center gap-3">
                      <WvcLogo className="h-7 w-auto" />
                      <div className="flex flex-col leading-none border-l border-primary pl-3">
                        <span className="font-default text-[10px] tracking-[0.22em] uppercase text-foreground">
                          // ODDHATTER
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto px-6 py-10">
                    <p className="font-default text-[10px] tracking-[0.22em] uppercase text-foreground mb-6">
                      // MENU
                    </p>
                    <MenuProvider menu_id="43">
                      <MobileMenuConsumer onNavigate={() => setMobileOpen(false)} />
                    </MenuProvider>
                  </div>

                  <div className="border-t border-primary px-6 py-6 space-y-4">
                    <Button asChild className="w-full bg-background text-white border-2 border-foreground hover:bg-secondary rounded-md uppercase tracking-[0.18em] font-default font-medium text-[16px] h-12">
                      <Link to="/ShopPage" onClick={() => setMobileOpen(false)}>
                        Get Tickets
                      </Link>
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      <div className={`overflow-hidden border-t border-primary bg-background/90 backdrop-blur-sm transition-all duration-300 ease-out ${searchOpen ? "max-h-40 opacity-100" : "max-h-0 opacity-0"}`}>
        <div className="mx-auto w-full max-w-[1400px] px-4 sm:px-6 lg:px-10 py-6">
          <div className="flex items-center gap-4">
            <span className="font-default text-[10px] tracking-[0.22em] uppercase text-foreground shrink-0 hidden sm:inline">
              // SEARCH
            </span>
            <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Find shows, merch, or blog posts..." className="flex-1" />
            <Button type="button" variant="ghost" size="icon" onClick={() => setSearchOpen(false)} className="text-foreground hover:bg-secondary hover:text-white shrink-0" aria-label="Close search">
              <X className="size-5" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
      </div>
    </section>;
}